import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { set, get, cloneDeep, isBoolean } from 'lodash';
import { useSelector } from 'react-redux';
import { Typeahead } from 'react-bootstrap-typeahead';
import { useTranslation } from 'react-i18next';
import { useProductCostingState } from '../ProductCostingContext';
import { checkCurrency, checkIsNumber, roundOff, roundingOff, restrictToTwoDecimals } from '../../../../Components/helper';
import { detailedCostingFieldpath, errorpath } from '../fieldPath';
import useShowHideSectionHooks from '../Hooks/useShowHideSectionHooks';
import useDetailedPageHook from '../Hooks/useDetailedPageHook';
import useDetailedCostingHooks from '../Hooks/useDetailedCostingHooks';
import useValidationHooks from '../Hooks/useValidationHooks';
import BombiixAGGrid, { HierExpandCell, ExpandChevronCell } from '../../../../Components/BombiixAGGrid';
import type { BombiixCellRendererParams, BombiixColumnDefOrGroup, BombiixFillBatchItem, BombiixRowId } from '../../../../Components/BombiixAGGrid/types';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import type { DetailedCostingState } from '../types';
import { useLengthLabel } from '../../../../hooks/useLengthLabel';
import { DeleteIcon, UploadIcon } from '../../../../bombiix-icon';

/** Delete-BOM row action glyph. */
const ICON_SIZE_ACTION = 20;
/** Import-all button glyph, sized to the compact button it sits in. */
const ICON_SIZE_IMPORT = 18;

/** Anything the table displays by `name`. */
interface NamedRef {
  _id?: string;
  name?: string;
}

/**
 * One BOM line under a BOM-type block, and the block itself, with the fields this table reads
 * narrowed. Declared standalone rather than as an intersection with `DetailedCostingExpandRow` /
 * `DetailedCostingBomRow`: intersecting the two `expandData` ARRAY types collapses the element
 * type to `{}`, which loses every field. The index signature keeps the rest of the document open.
 */
type BomExpandRow = {
  colourway?: { name?: string; colourRef?: string; newReferences?: string } | undefined;
  size?: string | undefined;
  length?: NamedRef | undefined;
  quantityPerSizCol?: unknown;
  quantityUnitOfMeasure?: NamedRef | undefined;
  pricePerUnit?: unknown;
  totalPrice?: unknown;
  isExcluded?: boolean | undefined;
  isChangedBySupplierTotalQuantityPerSizCol?: boolean | undefined;
  isChangedBySupplierPricePerUnit?: boolean | undefined;
  [key: string]: unknown;
};

type BomSourceRow = {
  bomType?: NamedRef | undefined;
  referenceCode?: string | undefined;
  bomDescription?: string | undefined;
  newPlacement?: NamedRef[] | undefined;
  isExpanded?: boolean | undefined;
  expandData?: BomExpandRow[] | undefined;
  totColourways?: string[] | undefined;
  totColourRefs?: string[] | undefined;
  totSize?: string[] | undefined;
  totLength?: string[] | undefined;
  overAllQuantity?: unknown;
  overAllTotalPrice?: unknown;
  [key: string]: unknown;
};

/** One entry of the BOM component library, as offered by the typeahead. */
interface BomListOption {
  _id?: string;
  referenceCode?: string;
  [key: string]: unknown;
}

/** The redux slices this table reads. */
interface DetailedCostingRootState {
  newProductCostingData: { bomCompoList?: BomListOption[]; productCostingPreviousData?: Record<string, unknown> };
  BOMConfigDetails: { tabs?: Array<Record<string, unknown>>; isProductArchived?: boolean };
  UserDetails: { isSupplierLoggedIn?: boolean };
}

/** The `clear()` slice of the react-bootstrap-typeahead instance API this table drives. */
interface TypeaheadInstance {
  clear: () => void;
}

/**
 * One rendered row. Declared with `type` rather than `interface`: BombiixAGGrid constrains its row
 * generic to `Record<string, unknown> & {…}`, and only type aliases receive the implicit index
 * signature.
 */
type DetailedCostingGridRow = {
  __cagId: string;
  __rowType: 'summary' | 'data';
  parentIndex?: number | undefined;
  chIndex?: number | undefined;
  __isFirst?: boolean | undefined;
  __showChevron?: boolean | undefined;
  isExpanded?: boolean | undefined;
  bomType?: string | undefined;
  referenceCode?: string | undefined;
  description?: string | undefined;
  placement?: string | undefined;
  colourway?: string | undefined;
  colourRef?: string | undefined;
  size?: string | undefined;
  length?: string | undefined;
  quantity?: unknown;
  uom?: string | undefined;
  price?: string | undefined;
  total?: string | undefined;
  quantityUnitOfMeasure?: NamedRef | undefined;
  pricePerUnit?: unknown;
  totalPrice?: unknown;
  isExcluded?: boolean | undefined;
  qtyAmber?: boolean | undefined;
  priceAmber?: boolean | undefined;
  qtyErr?: string | undefined;
  priceErr?: string | undefined;
  uomErr?: string | undefined;
};

interface ExpandAllHeaderProps {
  allExpanded: boolean;
  onToggleAll: (next: boolean) => void;
}

/**
 * Expand/collapse-all chevron for this table's header.
 *
 * BombiixAGGrid ships its own ExpandAllHeader, but that one drives AG Grid master/detail nodes.
 * This table fakes its hierarchy with flat rows, so expansion is caller-owned and so is the header.
 */
function ExpandAllHeader(props: ExpandAllHeaderProps): React.JSX.Element {
  const { allExpanded, onToggleAll } = props;
  return (
    <div className="cag-header cag-header--expand-all">
      <ExpandChevronCell
        expanded={Boolean(allExpanded)}
        onToggle={() => onToggleAll(!allExpanded)}
        expandLabel="Expand all"
        collapseLabel="Collapse all"
      />
    </div>
  );
}

/**
 * Renders the "Exclude" checkbox for data rows.
 */
function ExcludeCell(params: BombiixCellRendererParams<DetailedCostingGridRow>): React.JSX.Element | null {
  const { value, data, colDef, onRowChange, getRowId, disabled } = params;
  const rowId: BombiixRowId | undefined = getRowId && data ? getRowId(data) : (data as { id?: BombiixRowId } | undefined)?.id;
  const field = colDef?.field;
  const [checked, setChecked] = useState<boolean>(Boolean(value));

  useEffect(() => {
    setChecked(Boolean(value));
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setChecked(event.target.checked);
    onRowChange?.(rowId as BombiixRowId, field as string, event.target.checked);
  };

  if (data?.__rowType !== 'data') return null;

  return (
    <label className="cag-checkbox mb-0">
      <input type="checkbox" className="cag-checkbox-input" checked={checked} disabled={disabled} onChange={handleChange} />
    </label>
  );
}

type ActionCellProps = BombiixCellRendererParams<DetailedCostingGridRow> & {
  onDelete?: (row: DetailedCostingGridRow | undefined) => void;
  isParentRow?: (row: DetailedCostingGridRow | undefined) => boolean;
  canDelete?: boolean;
  deleteDisabled?: boolean;
};

/**
 * Renders the delete action button on parent rows.
 */
function ActionCell(params: ActionCellProps): React.JSX.Element | null {
  const { data, onDelete, isParentRow, canDelete, deleteDisabled } = params;
  const showDelete = canDelete && isParentRow?.(data);

  if (!showDelete) return null;

  return (
    <div className="d-flex align-items-center">
      <button
        type="button"
        className="bombiix-icon-container bombiix-icon-container--red"
        title="Delete BOM"
        aria-label="Delete BOM"
        disabled={deleteDisabled}
        onClick={() => onDelete?.(data)}
      >
        <DeleteIcon size={ICON_SIZE_ACTION} />
      </button>
    </div>
  );
}

const truncateAfter20Words = (text?: string): string => {
  if (!text) return '';
  const words = text.split(' ');
  if (words.length <= 20) return text;
  return `${words.slice(0, 20).join(' ')}...`;
};

export function DetailedCostingTable(): React.JSX.Element {
  const {
    setDetailedCostingState,
    detailedCostingState,
    productCostingstate,
    colSizeLengthToggleState,
    isAllExpandedState,
    setExpandAllState,
    globalError,
    CoastingAddEditRights,
    CoastingDeleteRights,
    initialOptionsState,
    landedInfoStates,
    internalState,
  } = useProductCostingState();
  const { primaryCurrValue } = internalState;

  /**
   * Resolves the effective exchange rate for the BOM detailed-costing conversion.
   * The BOM table is not rendered per distribution centre, so it follows the first
   * DC's exchange-rate field (manual rate when that DC's toggle is on, otherwise the
   * global primary currency value) to match the on-load conversion logic.
   */
  const getDcZeroExchangeRate = (): string | number => {
    const exchangeInfo = landedInfoStates?.[0]?.landedExchangeRateInformation;
    return exchangeInfo?.manualAdded ? exchangeInfo?.exchangedRate ?? 1 : primaryCurrValue ?? 1;
  };
  const { t } = useTranslation('common');
  const lengthLabel = useLengthLabel(t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.LENGTH'));
  const typeaheadRef = useRef<TypeaheadInstance | null>(null);
  const detailedCostingStateRef = useRef<DetailedCostingState>(detailedCostingState);
  detailedCostingStateRef.current = detailedCostingState;

  const {
    productInfo: { supplierId, currency },
    isGlobalUrl,
  } = productCostingstate;
  /* `supplierId` holds a populated supplier object on this state, not an id string — the same
     mismatch already documented in useDetailedPageHook. */
  const supplierRef = supplierId as unknown as { _id?: string } | null;

  const { bomAllExpand } = isAllExpandedState;
  const {
    detailedCosting: { bomTypeArr },
  } = detailedCostingState;
  const detailedCostingErr = globalError.detailedCostingErr;
  const { uomOptionsArr } = initialOptionsState;

  const { renderDetailedCostingArr } = useDetailedPageHook();
  const { calculateDetailCosting } = useDetailedCostingHooks();
  const { onChangeCheckIfErrorExist } = useValidationHooks();
  const { hideLengthCol, hideColourCol, onLoadDisable, hideSizeCol, statusState, isDisabled, isSupplierView, controlAmberColour, markAsCompleteState } =
    useShowHideSectionHooks();

  const {
    newProductCostingData: { bomCompoList, productCostingPreviousData },
    BOMConfigDetails: { tabs, isProductArchived },
    UserDetails: { isSupplierLoggedIn },
  } = useSelector((state: DetailedCostingRootState) => state);
  const { colourWayToggle, sizeLengthToggle } = colSizeLengthToggleState;

  const expandDetailedCostingChild = (index: number | null, expandAll?: boolean): void => {
    setDetailedCostingState((state) => {
      const copyState: DetailedCostingState = cloneDeep(state);
      if (isBoolean(expandAll)) {
        copyState.detailedCosting.bomTypeArr = copyState?.detailedCosting?.bomTypeArr?.map((val) => ({ ...val, isExpanded: expandAll as boolean }));
      } else if ((index ?? -1) >= 0) {
        const path = `detailedCosting.bomTypeArr[${index}].isExpanded`;
        set(copyState, path, !get(copyState, path, false));
      }
      return { ...state, ...copyState };
    });
    if (isBoolean(expandAll)) {
      setExpandAllState((prev) => ({ ...prev, bomAllExpand: expandAll as boolean }));
    } else if (bomAllExpand) {
      setExpandAllState((prev) => ({ ...prev, bomAllExpand: false }));
    }
  };

  const handleInputChange = async (value: unknown, path: string, supplierPath?: string): Promise<void> => {
    const copyState: DetailedCostingState = cloneDeep(detailedCostingState);
    set(copyState, path, value);
    if (supplierPath) {
      if (isSupplierLoggedIn || isGlobalUrl) {
        const previousValue = get(productCostingPreviousData, path);
        set(copyState, supplierPath, previousValue !== value);
      } else {
        set(copyState, supplierPath, false);
      }
    }
    await calculateDetailCosting(copyState, getDcZeroExchangeRate(), false, currency);
  };

  const handleUomChange = async (value: unknown, bomPath: string): Promise<void> => {
    const copyState: DetailedCostingState = cloneDeep(detailedCostingState);
    set(copyState, bomPath, value);
    setDetailedCostingState((state) => ({ ...state, ...copyState }));
  };

  const handleDeleteBom = async (row?: DetailedCostingGridRow): Promise<void> => {
    const copyState: DetailedCostingState = cloneDeep(detailedCostingStateRef.current);
    const list = copyState?.detailedCosting?.bomTypeArr || [];
    copyState.detailedCosting.bomTypeArr = list.filter((_, idx) => idx !== row?.parentIndex);
    await calculateDetailCosting(copyState, getDcZeroExchangeRate(), false, currency);
  };

  const isParentRow = (row?: DetailedCostingGridRow): boolean => row?.__rowType === 'summary' || (row?.__rowType === 'data' && Boolean(row?.__isFirst));

  const updateError = (path: string): void => onChangeCheckIfErrorExist(path, globalError);

  const bomListOptions = useMemo<BomListOption[]>(
    () => ((bomCompoList?.length ?? 0) > 0 ? (bomCompoList as BomListOption[]) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bomCompoList?.length],
  );

  const handleBomSelect = async (selected: BomListOption[], isAllBom?: boolean): Promise<void> => {
    if (isAllBom) {
      renderDetailedCostingArr(selected, Boolean(isAllBom), colourWayToggle, sizeLengthToggle, supplierRef?._id);
    } else {
      renderDetailedCostingArr(selected?.[0] as BomListOption, Boolean(isAllBom), colourWayToggle, sizeLengthToggle, supplierRef?._id);
    }
    if (typeaheadRef?.current) typeaheadRef.current.clear();
  };

  const rearrangeBomTypeArr = useMemo<BomSourceRow[]>(() => {
    const list = bomTypeArr as BomSourceRow[];
    if (!list?.length || !tabs?.length) return list;
    const getSequenceIds = tabs?.map((vl) => vl?._id);
    return list.sort((a, b) => {
      const indexA = getSequenceIds?.indexOf(a?.bomType?._id);
      const indexB = getSequenceIds?.indexOf(b?.bomType?._id);
      return indexA - indexB;
    });
  }, [bomTypeArr, tabs]);

  const shouldShowExpand = hideColourCol || hideLengthCol || hideSizeCol;

  const rowData = useMemo<DetailedCostingGridRow[]>(() => {
    const rows: DetailedCostingGridRow[] = [];
    const list: BomSourceRow[] = Array.isArray(rearrangeBomTypeArr) ? rearrangeBomTypeArr : [];

    list.forEach((bomData, index) => {
      const expandData = bomData?.expandData || [];
      const isCollapsed = !bomData?.isExpanded && expandData.length > 1;
      const placement = bomData?.newPlacement?.map((vl) => vl?.name)?.join(', ') || '--';

      if (isCollapsed) {
        rows.push({
          __cagId: `b${index}-sum`,
          __rowType: 'summary',
          parentIndex: index,
          __showChevron: true,
          isExpanded: false,
          bomType: bomData?.bomType?.name,
          referenceCode: bomData?.referenceCode,
          description: bomData?.bomDescription,
          placement,
          colourway: bomData?.totColourways?.join(', ') || '--',
          colourRef: bomData?.totColourRefs?.join(', ') || '--',
          size: bomData?.totSize?.join(', ') || '--',
          length: bomData?.totLength?.join(', ') || '--',
          quantity: roundingOff(bomData?.overAllQuantity, 2) || '--',
          uom: '--',
          price: '--',
          total: bomData?.overAllTotalPrice ? roundingOff(bomData.overAllTotalPrice, 2) : '--',
        });
        return;
      }

      if (!(bomData?.isExpanded || expandData.length === 1)) return;

      expandData.forEach((child, chIndex) => {
        const isFirst = chIndex === 0;
        rows.push({
          __cagId: `b${index}-c${chIndex}`,
          __rowType: 'data',
          parentIndex: index,
          chIndex,
          __isFirst: isFirst,
          __showChevron: isFirst && expandData.length > 1,
          isExpanded: bomData?.isExpanded,
          bomType: isFirst ? bomData?.bomType?.name || '--' : '',
          referenceCode: isFirst ? bomData?.referenceCode || '--' : '',
          description: isFirst ? bomData?.bomDescription : '',
          placement: isFirst ? placement : '',
          colourway: child?.colourway?.name || '--',
          colourRef: child?.colourway?.colourRef || child?.colourway?.newReferences || '--',
          size: child?.size || '--',
          length: child?.length?.name || '--',
          quantity: child?.quantityPerSizCol,
          quantityUnitOfMeasure: child?.quantityUnitOfMeasure,
          pricePerUnit: child?.pricePerUnit,
          totalPrice: child?.totalPrice,
          isExcluded: child?.isExcluded,
          qtyAmber: child?.isChangedBySupplierTotalQuantityPerSizCol && controlAmberColour,
          priceAmber: child?.isChangedBySupplierPricePerUnit && controlAmberColour,
          qtyErr: get(detailedCostingErr, `[${index}][${chIndex}].quantityPerSizCol`, ''),
          priceErr: get(detailedCostingErr, `[${index}][${chIndex}].pricePerUnit`, ''),
          uomErr: get(detailedCostingErr, `[${index}][${chIndex}].quantityUnitOfMeasure`, ''),
        });
      });
    });
    return rows;
  }, [rearrangeBomTypeArr, controlAmberColour, detailedCostingErr]);

  const onRowChange = useCallback(
    (rowId: BombiixRowId, field: string, value: unknown) => {
      const row = rowData.find((item) => item.__cagId === rowId);
      if (!row || row.__rowType !== 'data') return;
      const { parentIndex, chIndex } = row;
      const fp = detailedCostingFieldpath(parentIndex, chIndex);

      if (field === 'quantity') {
        const restricted = restrictToTwoDecimals(value);
        if (!checkIsNumber(restricted || 0)) return;
        handleInputChange(restricted, fp.EXPAND_QUANTITY_PER_SIZ_COL, fp.IS_CHANGED_BY_SUPPLIER_QUANTITY_PER_SIZE_COl);
        if (get(detailedCostingErr, `[${parentIndex}][${chIndex}].quantityPerSizCol`, '')) updateError(errorpath(parentIndex, chIndex).DETAILED.ERROR_QUANTITY);
        return;
      }
      if (field === 'pricePerUnit') {
        const restricted = restrictToTwoDecimals(value);
        if (!checkIsNumber(restricted || 0)) return;
        handleInputChange(restricted, fp.EXPAND_PRICE_PER_UNIT, fp.IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT);
        if (get(detailedCostingErr, `[${parentIndex}][${chIndex}].pricePerUnit`, ''))
          updateError(errorpath(parentIndex, chIndex).DETAILED.ERROR_PRICE_PER_UNIT);
        return;
      }
      if (field === 'quantityUnitOfMeasure') {
        handleUomChange(value, fp.BOM_QUANTITY_UOM);
        if (get(detailedCostingErr, `[${parentIndex}][${chIndex}].quantityUnitOfMeasure`, '')) updateError(errorpath(parentIndex, chIndex).DETAILED.ERROR_UOM);
        return;
      }
      if (field === 'isExcluded') {
        handleInputChange(value, fp.IS_EXCLUDED);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowData, detailedCostingErr],
  );

  const handleFillCells = async (changes: BombiixFillBatchItem<DetailedCostingGridRow>[]): Promise<void> => {
    if (!Array.isArray(changes) || changes.length === 0) return;
    const copyState: DetailedCostingState = cloneDeep(detailedCostingStateRef.current);
    const supplierActive = isSupplierLoggedIn || isGlobalUrl;
    let needsCalc = false;

    const applyNumeric = (valuePath: string, supplierPath: string, rawValue: unknown): boolean => {
      const restricted = restrictToTwoDecimals(rawValue);
      if (!checkIsNumber(restricted || 0)) return false;
      set(copyState, valuePath, restricted);
      set(copyState, supplierPath, supplierActive ? get(productCostingPreviousData, valuePath) !== restricted : false);
      return true;
    };

    changes.forEach((change) => {
      const row = rowData.find((item) => item.__cagId === change.rowId);
      if (!row || row.__rowType !== 'data') return;
      const fp = detailedCostingFieldpath(row.parentIndex, row.chIndex);

      switch (change.field) {
        case 'quantity':
          if (applyNumeric(fp.EXPAND_QUANTITY_PER_SIZ_COL, fp.IS_CHANGED_BY_SUPPLIER_QUANTITY_PER_SIZE_COl, change.newValue)) needsCalc = true;
          break;
        case 'pricePerUnit':
          if (applyNumeric(fp.EXPAND_PRICE_PER_UNIT, fp.IS_CHANGED_BY_SUPPLIER_PRICE_PER_UNIT, change.newValue)) needsCalc = true;
          break;
        case 'quantityUnitOfMeasure':
          set(copyState, fp.BOM_QUANTITY_UOM, change.newValue);
          break;
        default:
          break;
      }
    });

    if (needsCalc) {
      await calculateDetailCosting(copyState, getDcZeroExchangeRate(), false, currency);
    } else {
      setDetailedCostingState((state) => ({ ...state, ...copyState }));
    }
  };

  const columnDefs = useMemo<BombiixColumnDefOrGroup<DetailedCostingGridRow>[]>(() => {
    const cols: BombiixColumnDefOrGroup<DetailedCostingGridRow>[] = [];

    if (shouldShowExpand) {
      cols.push({
        colId: '__expand',
        headerName: '',
        width: 40,
        minWidth: 40,
        maxWidth: 40,
        sortable: false,
        resizable: false,
        suppressHeaderMenuButton: true,
        cellRenderer: HierExpandCell,
        cellProps: {
          isParent: (row: DetailedCostingGridRow) => row.__showChevron,
          getExpanded: (row: DetailedCostingGridRow) => row.isExpanded,
          onToggle: (row: DetailedCostingGridRow) => expandDetailedCostingChild(row.parentIndex ?? null),
        },
        headerComponent: ExpandAllHeader,
        headerComponentParams: { allExpanded: bomAllExpand, onToggleAll: (val: boolean) => expandDetailedCostingChild(null, val) },
      });
    }

    cols.push({
      field: 'bomType',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.BOM_TYPE'),
      cellType: 'readonlyText',
      width: 140,
      flex: 1,
      sortable: false,
      cellProps: { accessor: (row: DetailedCostingGridRow) => row.bomType, fallback: '' },
    });
    cols.push({
      field: 'referenceCode',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.COMPONENT_CODE'),
      cellType: 'readonlyText',
      width: 158,
      flex: 1,
      sortable: false,
      cellProps: { accessor: (row: DetailedCostingGridRow) => row.referenceCode, fallback: '' },
    });
    cols.push({
      field: 'description',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.COMPONENT_DESCRIPTION'),
      cellType: 'readonlyText',
      minWidth: 364,
      flex: 1,
      sortable: false,
      cellProps: {
        accessor: (row: DetailedCostingGridRow) => (row.__rowType === 'data' ? truncateAfter20Words(row.description) : row.description),
        fallback: '',
      },
    });
    cols.push({
      field: 'placement',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.PLACEMENT'),
      cellType: 'readonlyText',
      width: 150,
      flex: 1,
      sortable: false,
      cellProps: { accessor: (row: DetailedCostingGridRow) => row.placement, fallback: '' },
    });

    if (hideColourCol) {
      cols.push({
        field: 'colourway',
        headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.COLOURWAY'),
        cellType: 'readonlyText',
        width: 200,
        flex: 1,
        sortable: false,
        cellProps: { accessor: (row: DetailedCostingGridRow) => row.colourway },
      });
      cols.push({
        field: 'colourRef',
        headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.COLOUR_REF'),
        cellType: 'readonlyText',
        width: 150,
        flex: 1,
        sortable: false,
        cellProps: { accessor: (row: DetailedCostingGridRow) => row.colourRef },
      });
    }
    if (hideSizeCol) {
      cols.push({
        field: 'size',
        headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.SIZE'),
        cellType: 'readonlyText',
        width: 120,
        flex: 1,
        sortable: false,
        cellProps: { accessor: (row: DetailedCostingGridRow) => row.size },
      });
    }
    if (hideLengthCol) {
      cols.push({
        field: 'length',
        headerName: lengthLabel,
        cellType: 'readonlyText',
        width: 120,
        flex: 1,
        sortable: false,
        cellProps: { accessor: (row: DetailedCostingGridRow) => row.length },
      });
    }

    cols.push({
      field: 'quantity',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.QUANTITY'),
      required: true,
      cellType: 'text',
      width: 107,
      flex: 1,
      sortable: false,
      getCellReadOnly: (row: DetailedCostingGridRow) => row.__rowType !== 'data' || statusState || Boolean(isProductArchived) || !CoastingAddEditRights,
      getCellError: (row: DetailedCostingGridRow) => row.qtyErr,
      getCellClass: (row: DetailedCostingGridRow) => (row.qtyAmber ? 'suggested-data' : ''),
      cellProps: { type: 'number', accessor: (row: DetailedCostingGridRow) => (row.__rowType === 'data' ? roundOff(row.quantity, 2) : row.quantity) },
    });
    cols.push({
      field: 'quantityUnitOfMeasure',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.UOM'),
      required: true,
      cellType: 'select',
      width: 100,
      flex: 1,
      sortable: false,
      getCellReadOnly: (row: DetailedCostingGridRow) => row.__rowType !== 'data' || isDisabled || statusState || !CoastingAddEditRights,
      getCellError: (row: DetailedCostingGridRow) => row.uomErr,
      cellProps: {
        options: uomOptionsArr,
        getOptionLabel: (opt: NamedRef) => opt?.name,
        getOptionValue: (opt: NamedRef) => opt?.name,
        accessor: (row: DetailedCostingGridRow) => (row.__rowType === 'data' ? row.quantityUnitOfMeasure?.name || '--' : row.uom),
      },
    });
    cols.push({
      field: 'pricePerUnit',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.PRICE_PER_UNIT'),
      required: true,
      cellType: 'currency',
      width: 129,
      flex: 1,
      sortable: false,
      getCellReadOnly: (row: DetailedCostingGridRow) => row.__rowType !== 'data' || statusState || Boolean(isProductArchived) || !CoastingAddEditRights,
      getCellError: (row: DetailedCostingGridRow) => row.priceErr,
      getCellClass: (row: DetailedCostingGridRow) => (row.priceAmber ? 'suggested-data' : ''),
      cellProps: { accessor: (row: DetailedCostingGridRow) => (row.__rowType === 'data' ? roundOff(row.pricePerUnit, 2) : row.price) },
    });
    cols.push({
      field: 'totalPrice',
      headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.TOTAL_PRICE'),
      cellType: 'readonlyText',
      width: 120,
      flex: 1,
      sortable: false,
      cellProps: {
        accessor: (row: DetailedCostingGridRow) =>
          row.__rowType === 'data' ? `${checkCurrency(currency, row.totalPrice)}${roundOff(row.totalPrice, 2, true)}` : row.total,
      },
    });
    if (!isSupplierView) {
      cols.push({
        field: 'isExcluded',
        headerName: t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.EXCLUDE'),
        width: 72,
        sortable: false,
        cellClass: 'cag-cell-center',
        headerClass: 'cag-header-center',
        cellRenderer: ExcludeCell,
        getCellDisabled: () => Boolean(markAsCompleteState || isProductArchived || !CoastingAddEditRights),
      });
      cols.push({
        colId: '__action',
        headerName: '',
        width: 40,
        minWidth: 40,
        sortable: false,
        cellRenderer: ActionCell,
        cellProps: {
          onDelete: handleDeleteBom,
          isParentRow,
          canDelete: CoastingDeleteRights,
          deleteDisabled: markAsCompleteState || isProductArchived,
        },
      });
    }

    return cols;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldShowExpand,
    hideColourCol,
    hideSizeCol,
    hideLengthCol,
    bomAllExpand,
    statusState,
    isProductArchived,
    CoastingAddEditRights,
    isDisabled,
    uomOptionsArr,
    currency,
    isSupplierView,
    markAsCompleteState,
    CoastingDeleteRights,
    t,
    lengthLabel,
  ]);

  return (
    <div className="costing-card p-12px">
      <div className="d-flex align-items-center mb-8">
        <p className="product-cp-title mb-0 mr-8"> {t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.BILL_OF_MATERIALS')}</p>
        {!isSupplierView && (
          <>
            <div className="testing-typeahead">
              <Typeahead
                id="object-options"
                labelKey="referenceCode"
                options={bomListOptions}
                onChange={handleBomSelect}
                multiple={false}
                minLength={1}
                placeholder={t('DEVLOPMENT_ORDER.EXPAND_VIEW.DETAILED_COSTING.SEARCH_IMPORT_BOM', { ns: 'costing' })}
                ref={typeaheadRef}
                disabled={onLoadDisable || statusState}
              />
            </div>{' '}
            &nbsp;
            <button
              disabled={onLoadDisable || isSupplierView || statusState}
              type="button"
              className="bombiix-icon-container bombiix-icon-container--blue ml-8"
              onClick={() => handleBomSelect(bomCompoList as BomListOption[], true)}
              aria-label="button"
            >
              <UploadIcon size={ICON_SIZE_IMPORT} />
            </button>
          </>
        )}
      </div>
      <BombiixAGGrid<DetailedCostingGridRow>
        hideCellErrorMessage
        revealHeaderControlsOnHover
        sortable={false}
        showColumnMenu={false}
        enableFillHandle
        onFillCells={handleFillCells}
        columnDefs={columnDefs}
        rowData={rowData}
        getRowId={(row) => row.__cagId}
        onRowChange={onRowChange}
        isRowReadOnly={(row) => row.__rowType !== 'data'}
        getRowClass={(row) => (row?.__rowType === 'summary' ? 'cag-hier-row--summary' : '')}
        currencySymbol={checkCurrency(currency, undefined, true)}
        autoSizeColumns
        gridOptions={{ rowHeight: 52, suppressMovableColumns: true }}
      />
    </div>
  );
}

export default DetailedCostingTable;
